import { FC, useState, useEffect, useRef } from 'react';
import Loader from 'components/Loader';
import { Toast } from 'components/Toast';
import { trackPixel } from 'helpers/pixel';
import { IPage } from 'store/donatePage/types';
import { IRecipient } from 'store/recipients/types';
import { CandidateType } from '../Candidates/Candidates';
import { Summary } from './Summary';
import { Recipient } from './Recipient';
import { getOnChangeCandidateAmount } from '../CustomSplit/utils';
import { GradientBorderCard } from 'components/Cards/GradientBorderCard';
import { CompleteDonationModal } from './CompleteDonationModal';
import { formatNumberAsCurrency } from 'helpers/utils';

interface Props {
  page?: IPage;
  isDafEnabled: boolean;
  candidates: CandidateType[];
  recipients: IRecipient[];
  selectedDonationAmount?: number;
  onDonateClick: (selectedMethod?: string, selectedMethodType?: string) => void;
  setCandidates: React.Dispatch<React.SetStateAction<CandidateType[]>>;
  setSelectedDonationAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
  onDafDonateClick: () => void;
}

export const RecSlate: FC<Props> = ({
  page,
  candidates,
  isDafEnabled,
  onDonateClick,
  recipients,
  setCandidates,
  selectedDonationAmount,
  setSelectedDonationAmount,
  onDafDonateClick,
}) => {
  const ref = useRef(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [candidatesWithDetails, setCandidatesWithDetails] = useState<CandidateType[]>([]);
  const [summaryWithShadow, setSummaryWithShadow] = useState(true);

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const observer = new IntersectionObserver(([entry]) => {
        setSummaryWithShadow(!entry.isIntersecting);
      });
      observer.observe(element);
    }
  }, []);

  useEffect(() => {
    const tempCandidatesWithDetails = candidates.map(candidate => ({
      ...candidate,
      ...page?.recipient_details?.[candidate.recipient_id],
    }));
    setCandidatesWithDetails(tempCandidatesWithDetails);
  }, [candidates]);

  const getOnDonateButtonClick = (selectedMethod?: string, selectedMethodType?: string) => () => {
    trackPixel('donation');
    onDonateClick(selectedMethod, selectedMethodType);
  };
  const onDonateButtonClick = getOnDonateButtonClick();

  const completeDonation = () => {
    setIsOpenModal(true);
  };

  if (!page || !recipients) {
    return <Loader width={60} height={60} />;
  }

  return (
    <div className="rec-slate-content">
      <Toast />
      <div className="rec-candidates-container">
        <div className="rec-candidates-content">
          <div className="row justify-between">
            <span className="body-small-text">{candidatesWithDetails.length} Recipients</span>
            <span className="body-small-text">Donation Amount</span>
          </div>
          {candidatesWithDetails.map((candidate, index) => (
            <Recipient
              key={candidate.recipient_id}
              candidate={candidate}
              index={index}
              onChangeAmount={getOnChangeCandidateAmount(
                candidates,
                setCandidates,
                setSelectedDonationAmount,
                true
              )}
            />
          ))}
          <div ref={ref} />
        </div>
        <div className={`rec-summary-container ${summaryWithShadow ? 'with-shadow' : ''}`}>
          <GradientBorderCard borderType="full" width="auto" borderSize={2}>
            <div className="inner-gradient-box">
              <Summary
                candidates={candidates}
                selectedDonationAmount={selectedDonationAmount}
                onClick={completeDonation}
              />
            </div>
          </GradientBorderCard>
        </div>
      </div>
      <CompleteDonationModal
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
        selectedDonationAmount={formatNumberAsCurrency(selectedDonationAmount || 0)}
        candidates={candidates}
        isClientPremium={page?.subscription_level === 'PREMIUM'}
        isDafEnabled={isDafEnabled}
        onDonateButtonClick={onDonateButtonClick}
        onDafDonateClick={onDafDonateClick}
        isWireEnabled={page?.wire_enabled}
        getOnDonateButtonClick={getOnDonateButtonClick}
        page={page}
      />
    </div>
  );
};
